import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Switch, Spin, Alert, Select, Tabs } from 'antd';
import { MessageOutlined, SaveOutlined, MobileOutlined, CloudServerOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const SMSConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [smsProvider, setSmsProvider] = useState('smsbao');
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getConfigs();
      
      let configsArray = [];
      if (response.code === 0) {
        if (Array.isArray(response.data)) {
          configsArray = response.data;
        } else if (response.data && response.data.list && Array.isArray(response.data.list)) {
          configsArray = response.data.list;
        } else if (response.data && typeof response.data === 'object') {
          configsArray = Object.entries(response.data).map(([key, value]) => ({
            config_key: key,
            config_value: value
          }));
        }
      }
      
      if (configsArray.length > 0) {
        const cfgMap = {};
        configsArray.forEach((item) => {
          cfgMap[item.config_key] = item.config_value;
        });

        form.setFieldsValue({
          sms_provider: cfgMap.sms_provider || 'smsbao',
          // 通用短信配置
          sms_sign: cfgMap.sms_sign || 'BlueAPI',
          sms_template: cfgMap.sms_template || '您的验证码是 %d，5分钟内有效。如非本人操作，请忽略。',
          // 短信宝配置
          smsbao_username: cfgMap.smsbao_username || '',
          smsbao_password: cfgMap.smsbao_password || '',
          // 阿里云配置
          aliyun_access_key_id: cfgMap.aliyun_access_key_id || '',
          aliyun_access_key_secret: cfgMap.aliyun_access_key_secret || '',
          aliyun_sign_name: cfgMap.aliyun_sign_name || '',
          aliyun_template_code: cfgMap.aliyun_template_code || '',
          // 腾讯云配置
          tencent_secret_id: cfgMap.tencent_secret_id || '',
          tencent_secret_key: cfgMap.tencent_secret_key || '',
          tencent_sms_sdk_app_id: cfgMap.tencent_sms_sdk_app_id || '',
          tencent_sign_name: cfgMap.tencent_sign_name || '',
          tencent_template_id: cfgMap.tencent_template_id || '',
        });
        
        setSmsProvider(cfgMap.sms_provider || 'smsbao');
      }
    } catch (error) {
      console.error('获取配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      
      const configsList = [
        { key: 'sms_provider', value: smsProvider },
        // 通用短信配置
        { key: 'sms_sign', value: values.sms_sign || '' },
        { key: 'sms_template', value: values.sms_template || '' },
        // 短信宝配置
        { key: 'smsbao_username', value: values.smsbao_username || '' },
        { key: 'smsbao_password', value: values.smsbao_password || '' },
        // 阿里云配置
        { key: 'aliyun_access_key_id', value: values.aliyun_access_key_id || '' },
        { key: 'aliyun_access_key_secret', value: values.aliyun_access_key_secret || '' },
        { key: 'aliyun_sign_name', value: values.aliyun_sign_name || '' },
        { key: 'aliyun_template_code', value: values.aliyun_template_code || '' },
        // 腾讯云配置
        { key: 'tencent_secret_id', value: values.tencent_secret_id || '' },
        { key: 'tencent_secret_key', value: values.tencent_secret_key || '' },
        { key: 'tencent_sms_sdk_app_id', value: values.tencent_sms_sdk_app_id || '' },
        { key: 'tencent_sign_name', value: values.tencent_sign_name || '' },
        { key: 'tencent_template_id', value: values.tencent_template_id || '' },
      ];

      for (const cfg of configsList) {
        await adminAPI.updateConfigByKey(cfg.key, cfg.value);
      }

      message.success('短信配置保存成功');
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    setTestLoading(true);
    try {
      const values = form.getFieldsValue();
      const response = await fetch('/api/admin/sms/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          phone: values.test_phone || '13800138000',
        }),
      });
      
      const data = await response.json();
      if (data.code === 0) {
        message.success('短信发送成功！');
      } else {
        message.error(data.message || '短信发送失败');
      }
    } catch (error) {
      message.error('短信发送失败，请检查配置');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="admin-page-wrapper">
      <div className="admin-page-container">
        <div className="page-header">
          <h2>短信配置</h2>
          <p>配置短信服务商，支持短信宝、阿里云和腾讯云短信服务</p>
        </div>

        <Spin spinning={loading}>
          <Form form={form} layout="vertical">
            {/* 短信服务说明 */}
            <Card 
              title={
                <span>
                  <MessageOutlined style={{ marginRight: 8 }} />
                  短信服务
                </span>
              }
              style={{ marginBottom: 24 }}
            >
              <Alert
                message="短信服务说明"
                description="用户可以使用手机号注册和登录。短信验证码用于注册、登录和找回密码等场景。"
                type="info"
                showIcon
              />
            </Card>

            <>
              {/* 短信服务商选择 */}
              <Card 
                  title={
                    <span>
                      <CloudServerOutlined style={{ marginRight: 8 }} />
                      选择短信服务商
                    </span>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Form.Item
                    name="sms_provider"
                    label="短信服务商"
                    extra="选择您要使用的短信服务商"
                  >
                    <Select
                      value={smsProvider}
                      onChange={setSmsProvider}
                      options={[
                        { value: 'smsbao', label: '短信宝 (推荐)' },
                        { value: 'aliyun', label: '阿里云短信服务' },
                        { value: 'tencent', label: '腾讯云短信服务' },
                      ]}
                    />
                  </Form.Item>
                </Card>

              {/* 短信模板配置（所有服务商通用） */}
              <Card
                title={<span><MessageOutlined style={{ marginRight: 8 }} />短信模板配置</span>}
                style={{ marginBottom: 24 }}
              >
                <Alert
                  message="短信签名和模板"
                  description="短信签名会显示在短信开头（如【灵狐算力】）。模板中的 %d 会被替换为验证码数字。"
                  type="info" showIcon style={{ marginBottom: 16 }}
                />
                <Form.Item name="sms_sign" label="短信签名" extra="短信开头的【签名】，用于标识发送方">
                  <Input placeholder="灵狐算力" />
                </Form.Item>
                <Form.Item name="sms_template" label="验证码模板" extra="使用 %d 表示验证码位置">
                  <Input.TextArea rows={2} placeholder="您的验证码是 %d，5分钟内有效。如非本人操作，请忽略。" />
                </Form.Item>
              </Card>

                {/* 短信宝配置 */}
                {smsProvider === 'smsbao' && (
                  <Card 
                    title={
                      <span>
                        <CloudServerOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                        短信宝配置
                      </span>
                    }
                    style={{ marginBottom: 24 }}
                  >
                    <Alert
                      message="获取短信宝账号"
                      description={
                        <span>
                          请前往 <a href="http://www.smsbao.com/" target="_blank" rel="noopener noreferrer">短信宝官网</a> 注册账号并充值短信条数。
                        </span>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name="smsbao_username"
                      label="短信宝用户名"
                      rules={[{ required: smsProvider === 'smsbao', message: '请输入短信宝用户名' }]}
                    >
                      <Input placeholder="短信宝注册用户名" />
                    </Form.Item>
                    <Form.Item
                      name="smsbao_password"
                      label="短信宝密码"
                      rules={[{ required: smsProvider === 'smsbao', message: '请输入短信宝密码' }]}
                      extra="短信宝登录密码（非MD5加密后的密码）"
                    >
                      <Input.Password placeholder="短信宝登录密码" />
                    </Form.Item>
                  </Card>
                )}

                {/* 阿里云配置 */}
                {smsProvider === 'aliyun' && (
                  <Card 
                    title={
                      <span>
                        <CloudServerOutlined style={{ marginRight: 8, color: '#ff6a00' }} />
                        阿里云短信配置
                      </span>
                    }
                    style={{ marginBottom: 24 }}
                  >
                    <Alert
                      message="获取阿里云短信配置"
                      description={
                        <span>
                          请前往 <a href="https://dysms.console.aliyun.com/" target="_blank" rel="noopener noreferrer">阿里云短信服务控制台</a> 获取 AccessKey 和短信模板信息。
                        </span>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name="aliyun_access_key_id"
                      label="AccessKey ID"
                    >
                      <Input.Password placeholder="阿里云 AccessKey ID" />
                    </Form.Item>
                    <Form.Item
                      name="aliyun_access_key_secret"
                      label="AccessKey Secret"
                    >
                      <Input.Password placeholder="阿里云 AccessKey Secret" />
                    </Form.Item>
                    <Form.Item
                      name="aliyun_sign_name"
                      label="短信签名"
                      extra="在阿里云短信服务中配置的签名名称"
                    >
                      <Input placeholder="例如：BlueAPI" />
                    </Form.Item>
                    <Form.Item
                      name="aliyun_template_code"
                      label="短信模板CODE"
                      extra="验证码短信模板CODE，模板中需包含 ${code} 变量"
                    >
                      <Input placeholder="例如：SMS_123456789" />
                    </Form.Item>
                  </Card>
                )}

                {/* 腾讯云配置 */}
                {smsProvider === 'tencent' && (
                  <Card 
                    title={
                      <span>
                        <CloudServerOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        腾讯云短信配置
                      </span>
                    }
                    style={{ marginBottom: 24 }}
                  >
                    <Alert
                      message="获取腾讯云短信配置"
                      description={
                        <span>
                          请前往 <a href="https://console.cloud.tencent.com/smsv2/" target="_blank" rel="noopener noreferrer">腾讯云短信服务控制台</a> 获取 SecretId、SecretKey 和短信模板信息。
                        </span>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name="tencent_secret_id"
                      label="SecretId"
                    >
                      <Input.Password placeholder="腾讯云 SecretId" />
                    </Form.Item>
                    <Form.Item
                      name="tencent_secret_key"
                      label="SecretKey"
                    >
                      <Input.Password placeholder="腾讯云 SecretKey" />
                    </Form.Item>
                    <Form.Item
                      name="tencent_sms_sdk_app_id"
                      label="SDK AppId"
                      extra="在腾讯云短信服务中创建的 SDK AppId"
                    >
                      <Input placeholder="例如：1400123456" />
                    </Form.Item>
                    <Form.Item
                      name="tencent_sign_name"
                      label="短信签名"
                      extra="在腾讯云短信服务中配置的签名内容"
                    >
                      <Input placeholder="例如：BlueAPI" />
                    </Form.Item>
                    <Form.Item
                      name="tencent_template_id"
                      label="模板ID"
                      extra="验证码短信模板ID，模板中需包含 {1} 变量作为验证码"
                    >
                      <Input placeholder="例如：1234567" />
                    </Form.Item>
                  </Card>
                )}

                {/* 测试功能 */}
                <Card 
                  title={
                    <span>
                      <MobileOutlined style={{ marginRight: 8 }} />
                      短信测试
                    </span>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Form.Item
                    name="test_phone"
                    label="测试手机号"
                    extra="输入手机号进行测试，测试将发送真实短信"
                  >
                    <Input placeholder="请输入手机号，如：13800138000" style={{ width: 300 }} />
                  </Form.Item>
                  <Button
                    type="default"
                    onClick={handleTestSMS}
                    loading={testLoading}
                    icon={<MobileOutlined />}
                  >
                    发送测试短信
                  </Button>
                </Card>
              </>

            {/* 保存按钮 */}
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              保存配置
            </Button>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default SMSConfig;
